class InfinitePageLoader extends HTMLElement {
  constructor() {
    super();

    this.sectionID =
      this.dataset.section ||
      document.querySelector("infinite-page-loader")?.dataset.section ||
      "";

    this.collectionInnerElement =
      document.querySelector("#product-grid");

    this.abortController = null;

    this.loading = false;
    this.destroyed = false;

    this.loaderObserver = null;
    this.productObserver = null;

    this.boundPageShow = this.restoreScrollPoint.bind(this);
    this.boundPopState = this.onPopState.bind(this);

    this.observedProducts = new WeakSet();

    this.currentPage = parseInt(this.dataset.currentpage || "1", 10);
    this.totalPages = parseInt(this.dataset.totalpages || "1", 10);

    this.restoreRequested = false;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;

    this.currentPage = parseInt(this.dataset.currentpage || "1", 10);
    this.totalPages = parseInt(this.dataset.totalpages || "1", 10);

    this.init();

    window.addEventListener("pageshow", this.boundPageShow);
    window.addEventListener("popstate", this.boundPopState);
  }

  disconnectedCallback() {
    this.destroyed = true;

    if (this.loaderObserver) {
      this.loaderObserver.disconnect();
      this.loaderObserver = null;
    }

    if (this.productObserver) {
      this.productObserver.disconnect();
      this.productObserver = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    window.removeEventListener("pageshow", this.boundPageShow);
    window.removeEventListener("popstate", this.boundPopState);
  }

  init() {
    this.createLoaderObserver();
    this.createProductObserver();

    this.observeProducts();
  }

  createLoaderObserver() {
    if (this.loaderObserver) {
      this.loaderObserver.disconnect();
    }

    this.loaderObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          this.loadNextPage();
        });
      },
      {
        root: null,
        rootMargin: "500px 0px",
        threshold: 0,
      }
    );

    this.loaderObserver.observe(this);
  }

  createProductObserver() {
    if (this.productObserver) return;

    this.productObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const card = entry.target;

          if (!card.dataset.page) return;

          this.updateCurrentURL(
            this.constructURLWithSearchParams(card.dataset.page)
          );
        });
      },
      {
        root: null,
        rootMargin: "250px 0px",
        threshold: 0,
      }
    );
  }

  observeProducts() {
    const cards =
      this.collectionInnerElement?.querySelectorAll(
        "marmeto-product-card"
      ) || [];

    cards.forEach((card) => {
      if (this.observedProducts.has(card)) return;

      this.observedProducts.add(card);

      this.productObserver.observe(card);

      card.addEventListener(
        "click",
        () => {
          if (card.id) {
            sessionStorage.setItem(
              "lastElementId",
              card.id
            );
          }
        },
        {
          passive: true,
          once: true,
        }
      );
    });
  }

  loadNextPage() {
    if (this.loading) return;

    if (this.currentPage >= this.totalPages) return;

    const url = this.constructURLWithSearchParams(
      this.currentPage + 1
    );

    this.loadProducts(url);
  }

  constructURLWithSearchParams(page) {
    const params = new URLSearchParams(window.location.search);

    const pageParam =
      this.dataset.pageParam || "page";

    params.set(pageParam, page);

    return `${window.location.pathname}?${params.toString()}`;
  }

  updateCurrentURL(url) {
    if (window.location.href === url) return;

    history.replaceState(
      {
        page: this.currentPage,
      },
      "",
      url
    );
  }

  onPopState() {
    this.restoreRequested = true;
  }

    async loadProducts(url) {
    if (this.loading || this.destroyed) return;

    this.loading = true;
    this.classList.add("loading");

    const spinner = this.querySelector("div");
    if (spinner) spinner.classList.remove("hidden");

    try {
      await this.fetchProducts(url);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Infinite Scroll:", error);
      }
    } finally {
      this.loading = false;
      this.classList.remove("loading");

      if (spinner) spinner.classList.add("hidden");
    }
  }

  async fetchProducts(url) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const requestURL = new URL(url, window.location.origin);

    requestURL.searchParams.set(
      "section_id",
      this.sectionID
    );

    const response = await fetch(requestURL.toString(), {
      signal: this.abortController.signal,
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error(
        `HTTP ${response.status}`
      );
    }

    const html = await response.text();

    const parser = new DOMParser();

    const documentHTML = parser.parseFromString(
      html,
      "text/html"
    );

    this.appendProducts(documentHTML);
  }

  appendProducts(documentHTML) {
    const newGrid =
      documentHTML.querySelector("#product-grid");

    if (!newGrid) {
      console.warn(
        "Infinite Scroll: #product-grid not found."
      );
      return;
    }

    this.collectionInnerElement.insertAdjacentHTML(
      "beforeend",
      newGrid.innerHTML
    );

    const loader =
      documentHTML.querySelector(
        "infinite-page-loader"
      );

    if (loader) {
      this.currentPage = parseInt(
        loader.dataset.currentpage || this.currentPage,
        10
      );

      this.totalPages = parseInt(
        loader.dataset.totalpages || this.totalPages,
        10
      );

      this.dataset.currentpage = this.currentPage;
      this.dataset.totalpages = this.totalPages;
    }

    this.observeProducts();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (window.HulkappWishlist?.init) {
          window.HulkappWishlist.init();
        }

        document.dispatchEvent(
          new CustomEvent(
            "infinite:page:loaded",
            {
              detail: {
                page: this.currentPage,
              },
            }
          )
        );
      });
    });
  }

  getNextPageURL() {
    if (this.currentPage >= this.totalPages) {
      return null;
    }

    return this.constructURLWithSearchParams(
      this.currentPage + 1
    );
  }

  refreshLoader() {
    if (!this.loaderObserver) return;

    this.loaderObserver.unobserve(this);
    this.loaderObserver.observe(this);
  }

  reloadObserver() {
    this.observeProducts();
    this.refreshLoader();
  }

    async restoreScrollPoint(event) {
    // pageshow fires when coming back from product page
    if (event && event.persisted) {
      this.restoreRequested = true;
    }

    const lastElementId = sessionStorage.getItem("lastElementId");

    if (!lastElementId) return;

    history.scrollRestoration = "manual";

    await this.waitForImages();
    await this.waitForElement(lastElementId);

    const element = document.getElementById(lastElementId);

    if (!element) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const y =
          element.getBoundingClientRect().top +
          window.pageYOffset -
          100;

        window.scrollTo({
          top: y,
          left: 0,
          behavior: "auto",
        });

        sessionStorage.removeItem("lastElementId");
        this.restoreRequested = false;
      });
    });
  }

  waitForElement(id) {
    return new Promise((resolve) => {
      const check = () => {
        const el = document.getElementById(id);

        if (el) {
          resolve(el);
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });
  }

  waitForImages() {
    const images = Array.from(document.images).filter(
      (img) => !img.complete
    );

    if (!images.length) {
      return Promise.resolve();
    }

    return Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener("load", resolve, {
              once: true,
            });

            img.addEventListener("error", resolve, {
              once: true,
            });
          })
      )
    );
  }

  saveScrollPoint() {
    const cards =
      this.collectionInnerElement?.querySelectorAll(
        "marmeto-product-card"
      ) || [];

    cards.forEach((card) => {
      if (card.dataset.scrollBound) return;

      card.dataset.scrollBound = "true";

      card.addEventListener(
        "click",
        () => {
          if (!card.id) return;

          sessionStorage.setItem(
            "lastElementId",
            card.id
          );

          sessionStorage.setItem(
            "scrollY",
            window.scrollY
          );

          sessionStorage.setItem(
            "currentPage",
            this.currentPage
          );
        },
        {
          passive: true,
        }
      );
    });
  }

  restoreSavedPosition() {
    const scrollY = Number(
      sessionStorage.getItem("scrollY")
    );

    if (!scrollY) return;

    requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollY,
        behavior: "auto",
      });
    });
  }

  updateHistoryPage(page) {
    const url = this.constructURLWithSearchParams(page);

    history.replaceState(
      {
        page,
      },
      "",
      url
    );
  }

  isLastPage() {
    return this.currentPage >= this.totalPages;
  }

  showLoader() {
    const loader = this.querySelector("div");

    if (loader) {
      loader.classList.remove("hidden");
    }
  }

  hideLoader() {
    const loader = this.querySelector("div");

    if (loader) {
      loader.classList.add("hidden");
    }
  }

    reinitializeWishlist() {
    if (
      window.HulkappWishlist &&
      typeof window.HulkappWishlist.init === "function"
    ) {
      try {
        window.HulkappWishlist.init();
      } catch (e) {
        console.warn("Wishlist initialization failed.", e);
      }
    }
  }

  refresh() {
    if (this.destroyed) return;

    this.observeProducts();
    this.refreshLoader();
    this.saveScrollPoint();
    this.reinitializeWishlist();
  }

  destroy() {
    this.destroyed = true;

    if (this.loaderObserver) {
      this.loaderObserver.disconnect();
      this.loaderObserver = null;
    }

    if (this.productObserver) {
      this.productObserver.disconnect();
      this.productObserver = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    window.removeEventListener(
      "pageshow",
      this.boundPageShow
    );

    window.removeEventListener(
      "popstate",
      this.boundPopState
    );
  }

  reset() {
    this.loading = false;
    this.destroyed = false;

    this.currentPage = parseInt(
      this.dataset.currentpage || "1",
      10
    );

    this.totalPages = parseInt(
      this.dataset.totalpages || "1",
      10
    );

    this.refresh();
  }
}

if (!customElements.get("infinite-page-loader")) {
  customElements.define(
    "infinite-page-loader",
    InfinitePageLoader
  );
}
/*
class InfinitePageLoader extends HTMLElement {
  constructor() {
    super();

    this.sectionID =
      this.dataset.section ||
      document.querySelector("infinite-page-loader")?.dataset.section ||
      "";
    this.collectionInnerElement = document.querySelector("#product-grid");
    this.saveScrollPoint();
    this.restoreScrollPoint();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addObserver();
  }

  addObserver() {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (entry.target == this.loadActivatorElem) observer.disconnect();

          var nextPageUrl = this.getNextPageUrl();

          if (nextPageUrl) this.loadProducts(nextPageUrl);
        }
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    observer.observe(this);
  }

  getNextPageUrl() {
    var nextPage = parseInt(this.dataset.currentpage) + 1;

    if (nextPage > this.dataset.totalpages) {
      return false;
    }

    return this.constructURLWithSearchParams(nextPage);
  }

  reintializeWishlist() {
    HulkappWishlist?.init();
  }

  constructURLWithSearchParams(page) {
    var params = new URLSearchParams(document.location.search);
    var nextPage = parseInt(page) || parseInt(this.dataset.currentpage) + 1;
    var pageParam = this.dataset.pageParam || "page";

    params.set(pageParam, nextPage);
    // params.set('filter.v.availability', 1) // To set the available products by default

    var searchParamsAsString = params.toString();

    return ""
      .concat(window.location.pathname, "?")
      .concat(searchParamsAsString);
  }

  updateCurrentURL(url) {
    if (history.replaceState) {
      window.history.pushState(
        {
          path: url,
        },
        "",
        url
      );
    }
  }

  loadProducts(url) {
    if (this.classList.contains("loading")) return;

    this.classList.add("loading");
    this.querySelector("div").classList.remove("hidden");

    this.getAndInsertHtml(url, "append");
  }

  getAndInsertHtml(url, insertPosition) {
    var _this4 = this;

    if (this.abortController) {
      this.abortController.abort();
    }
    var currentUrl = new URL(url, window.location.href);
    currentUrl.searchParams.set("section_id", this.sectionID);

    try {
      this.abortController = new AbortController();

      fetch(currentUrl.toString(), {
        signal: this.abortController.signal,
      }).then(function (response) {
        response.text().then(function (content) {
          var tempElement = new DOMParser().parseFromString(
            content,
            "text/html"
          );

          if (insertPosition == "append") {
            //Appending new sets of product cards
            _this4.collectionInnerElement.insertAdjacentHTML(
              "beforeend",
              tempElement.querySelector("#product-grid").innerHTML
            );
            _this4.dataset.currentpage = tempElement.querySelector(
              "infinite-page-loader"
            ).dataset.currentpage;
            _this4.dataset.totalpages = tempElement.querySelector(
              "infinite-page-loader"
            ).dataset.totalpages;
          } else if (insertPosition == "prepend") {
            _this4.collectionInnerElement.insertAdjacentHTML(
              "afterbegin",
              tempElement.querySelector("#product-grid").innerHTML
            );
            _this4.dataset.currentpage =
              tempElement.querySelector(
                "load-previous-page"
              ).dataset.currentpage;
          }

          _this4.init();
          // _this4.reintializeWishlist();
          _this4.saveScrollPoint();

          _this4.classList.remove("loading");
          _this4.querySelector("div").classList.add("hidden");
        });
      });
    } catch (exception) {}
  }

  saveScrollPoint() {
    let productCards = document.querySelectorAll("marmeto-product-card");

    productCards.forEach((productCard) => {
      const observer = new IntersectionObserver(
        (entries) => {
          observerCallback(entries, observer, productCard);
        },
        { threshold: 0.7 }
      );
      observer.observe(productCard); // Observing each product card that is visible

      productCard.addEventListener("click", () => {
        sessionStorage.setItem("lastElementId", productCard.id);
      });
    });

    const observerCallback = (entries, observer, header) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting && entry.target.dataset.page) {
          this.updateCurrentURL(
            this.constructURLWithSearchParams(entry.target.dataset.page)
          ); //updating the url depending on the product viewed
        }
      });
    };
  }

  restoreScrollPoint() {
    // Returning if browser is safari as it gets scrolled to top after scrollIntoView method is used.
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) return;

    history.scrollRestoration = "manual";
    // Reverting back to the previous element that was being viewed.
    window.onload = function () {
      var lastElementId = sessionStorage.getItem("lastElementId");
      var lastElement = document.getElementById(lastElementId);

      if (lastElement) lastElement.scrollIntoView();

      sessionStorage.removeItem("lastElementId");
    };
  }
}

customElements.define("infinite-page-loader", InfinitePageLoader);
*/
class LoadPreviousPage extends InfinitePageLoader {
  constructor() {
    super();

    this.addEventListener("click", this.loadProducts.bind(this));
  }

  connectedCallback() {
    this.init();
  }

  init() {
    var infinitePageLoader = document.querySelector("infinite-page-loader");
    var maxPagesLoadable = this.dataset.totalpages - 1;

    if (
      infinitePageLoader.dataset.currentpage - this.dataset.currentpage ==
        maxPagesLoadable || //when all the pages are loaded
      this.dataset.currentpage == 1
    ) {
      // When user is at page 1
      this.classList.add("hidden");
    } else {
      this.classList.remove("hidden");
    }
  }

  loadProducts() {
    if (this.classList.contains("loading")) return;

    var prevPageUrl = this.getPrevPageUrl();

    if (prevPageUrl) {
      this.classList.add("loading");
      this.getAndInsertHtml(prevPageUrl, "prepend");
    }
  }

  getPrevPageUrl() {
    var prevPage = parseInt(this.dataset.currentpage) - 1;

    if (prevPage < 1) {
      return false;
    }

    return this.constructURLWithSearchParams(prevPage);
  }
}

customElements.define("load-previous-page", LoadPreviousPage);
